// src/contexts/UserContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../utils/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const UserContext = createContext();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [clubId, setClubId] = useState(null);
  const [effectiveClubId, setEffectiveClubId] = useState(null);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.role || 'user');
            setClubId(userData.clubId || null);
            
            // Check if platform owner and calculate effective clubId
            const isOwner = userData.platformOwner === true;
            setIsPlatformOwner(isOwner);
            
            // Platform owner uses activeClubId if set, otherwise their own clubId
            const effective = isOwner 
              ? (userData.activeClubId || userData.clubId) 
              : userData.clubId;
            setEffectiveClubId(effective);
            
            if (isOwner && userData.activeClubId) {
              console.log('🔑 Platform owner active - using club:', userData.activeClubId);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserRole('user');
          setClubId(null);
          setEffectiveClubId(null);
          setIsPlatformOwner(false);
        }
      } else {
        setUser(null);
        setUserRole(null);
        setClubId(null);
        setEffectiveClubId(null);
        setIsPlatformOwner(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Function to refresh user data (e.g., after switching clubs)
  const refreshUserData = async () => {
    if (!user) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.role || 'user');
        setClubId(userData.clubId || null);
        
        const isOwner = userData.platformOwner === true;
        setIsPlatformOwner(isOwner);
        
        const effective = isOwner 
          ? (userData.activeClubId || userData.clubId) 
          : userData.clubId;
        setEffectiveClubId(effective);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  return (
    <UserContext.Provider value={{ 
      user, 
      userRole, 
      clubId,
      effectiveClubId,
      isPlatformOwner,
      loading,
      refreshUserData
    }}>
      {children}
    </UserContext.Provider>
  );
}

// Custom hook for easy access to user role
export function useUserRole() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserRole must be used within a UserProvider');
  }
  
  const { userRole, loading } = context;
  return {
    userRole,
    isAdmin: userRole === 'admin',
    isUser: userRole === 'user', 
    isViewer: userRole === 'viewer',
    loading
  };
}

// Custom hook for club access (respects platform owner switching)
export function useClub() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useClub must be used within a UserProvider');
  }
  
  const { clubId, effectiveClubId, isPlatformOwner, loading, refreshUserData } = context;
  return {
    clubId,           // User's actual club
    effectiveClubId,  // Club to use for operations (may be different for platform owner)
    isPlatformOwner,
    loading,
    refreshUserData
  };
}

// AdminOnly component - your global solution
export function AdminOnly({ children }) {
  const { isAdmin, loading } = useUserRole();
  
  if (loading) return null;
  if (!isAdmin) return null;
  
  return children;
}

// PlatformOwnerOnly component - for platform owner features
export function PlatformOwnerOnly({ children }) {
  const context = useContext(UserContext);
  
  if (context?.loading) return null;
  if (!context?.isPlatformOwner) return null;
  
  return children;
}
